<script setup lang="ts">
import { computed } from 'vue';
import { type Requirement } from '@/api/planning/requirements';
import { type WeeklyPlan } from '@/api/planning/weekly-plans';
import {
  WEEKDAYS_MONDAY_FIRST, WEEKDAY_NAMES_ES, WEEKDAY_INITIALS_ES,
} from '@/utils/monthly-expectation';
import { careerMetadata } from '@/utils/career-metadata';

const MINUTES_PER_HOUR = 60;
const NO_TIME_SORT_KEY = Number.MAX_SAFE_INTEGER;

interface Props {
  modelValue: boolean;
  requirements: Requirement[];
}

interface DaySlot {
  plan: WeeklyPlan;
  requirement: Requirement;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: 'update:modelValue', v: boolean): void }>();

function timeSortKey(slot: DaySlot): number {
  const time = slot.plan.fixedStartTime || slot.plan.timeWindowStart;
  if (!time) return NO_TIME_SORT_KEY;
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * MINUTES_PER_HOUR + (m ?? 0);
}

function slotTimeLabel(slot: DaySlot): string {
  if (slot.plan.fixedStartTime) return slot.plan.fixedStartTime;
  if (slot.plan.timeWindowStart && slot.plan.timeWindowEnd) {
    return `${slot.plan.timeWindowStart}–${slot.plan.timeWindowEnd}`;
  }
  return 'Hora flexible';
}

function slotDuration(slot: DaySlot): number {
  return slot.plan.durationMin ?? slot.requirement.durationMin;
}

function buildSlotsByDay(requirements: Requirement[]): Record<number, DaySlot[]> {
  const result: Record<number, DaySlot[]> = {};
  for (const day of WEEKDAYS_MONDAY_FIRST) result[day] = [];
  requirements.forEach((requirement) => {
    requirement.weeklyPlans.forEach((plan) => {
      plan.days.forEach((day) => {
        result[day]?.push({ plan, requirement });
      });
    });
  });
  return result;
}

const slotsByDay = computed(() => {
  const result = buildSlotsByDay(props.requirements);
  for (const day of WEEKDAYS_MONDAY_FIRST) {
    result[day]?.sort((a, b) => timeSortKey(a) - timeSortKey(b));
  }
  return result;
});

const totalSlots = computed(() => Object.values(slotsByDay.value)
  .reduce((acc, arr) => acc + arr.length, 0));

function close() {
  emit('update:modelValue', false);
}
</script>

<template>
  <v-dialog
    :model-value="modelValue"
    max-width="1080"
    scrollable
    @update:model-value="close"
  >
    <v-card rounded="lg">
      <v-card-title class="d-flex align-center ga-3 pa-4 pb-2">
        <v-icon
          icon="mdi-calendar-week"
          color="primary"
        />
        <div>
          <div class="text-h6 font-weight-bold">
            Vista semanal
          </div>
          <div class="text-caption text-medium-emphasis">
            Calendario tentativo según los planes semanales activos
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-4">
        <v-alert
          v-if="totalSlots === 0"
          type="info"
          variant="tonal"
          density="compact"
          icon="mdi-information-outline"
        >
          No hay planes semanales activos para mostrar.
        </v-alert>

        <div
          v-else
          class="weekly-grid"
        >
          <div
            v-for="day in WEEKDAYS_MONDAY_FIRST"
            :key="day"
            class="day-column"
          >
            <div class="day-header">
              <span class="day-initial">{{ WEEKDAY_INITIALS_ES[day] ?? '' }}</span>
              <span class="day-name">{{ WEEKDAY_NAMES_ES[day] ?? '' }}</span>
            </div>
            <div class="day-body">
              <v-card
                v-for="slot in slotsByDay[day]"
                :key="slot.plan.id"
                :color="careerMetadata(slot.requirement.career).color"
                variant="tonal"
                rounded="md"
                class="slot pa-2 mb-2"
              >
                <div class="d-flex align-center ga-2">
                  <v-icon
                    :icon="careerMetadata(slot.requirement.career).icon"
                    size="small"
                  />
                  <span class="text-body-2 font-weight-bold">
                    {{ slotTimeLabel(slot) }}
                  </span>
                </div>
                <div class="text-caption mt-1">
                  {{ slot.requirement.humanCareer }} · {{ slotDuration(slot) }} min
                </div>
                <div
                  v-if="slot.plan.caregiverFullName"
                  class="text-caption text-medium-emphasis mt-1 d-flex align-center ga-1"
                >
                  <v-icon
                    icon="mdi-account"
                    size="x-small"
                  />
                  {{ slot.plan.caregiverFullName }}
                </div>
              </v-card>
              <div
                v-if="!slotsByDay[day]?.length"
                class="empty-day text-caption text-medium-emphasis text-center py-2"
              >
                —
              </div>
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn
          text="Cerrar"
          variant="text"
          @click="close"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.weekly-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(120px, 1fr));
  gap: 8px;
}

.day-column {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.day-header {
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 8px 4px;
  border-bottom: 2px solid rgba(var(--v-theme-on-surface), .08);
  margin-bottom: 8px;
}

.day-initial {
  font-size: 1rem;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.day-name {
  font-size: .8rem;
  text-transform: uppercase;
  letter-spacing: .5px;
  color: rgba(var(--v-theme-on-surface), .6);
}

.day-body {
  flex: 1;
  min-height: 60px;
}

.slot {
  font-size: .85rem;
}

.empty-day {
  opacity: .5;
}

@media (max-width: 768px) {
  .weekly-grid {
    grid-template-columns: 1fr;
  }

  .day-body {
    min-height: 0;
  }

  .empty-day {
    display: none;
  }
}
</style>
